import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import Plotly from "plotly.js-dist-min";
import React, { useEffect, useMemo, useRef } from "react";
const COLOR_CHART_GREEN = "#1CC69E";
const COLOR_CHART_MARKER_BLUE = "#0066FF";
const COLOR_FILL = "rgba(28, 198, 158, 0.2)";
const COLOR_MARKER_BORDER = "rgba(74, 144, 226, 0.3)";
const COLOR_AXIS_FONT = "#9CA3AF";
const COLOR_TITLE_FONT = "#374151";
const COLOR_HOVER_BORDER = "#E5E7EB";
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 1000 * 3600 * 24;
const SPREAD_STEP_MIN_MS = 20 * 1000;
const SPREAD_STEP_GAP_RATIO = 0.12;
const SPREAD_STEP_MAX_MS = 2 * 60 * 60 * 1000;
const FALLBACK_GAP_MS = MS_PER_HOUR;
const RIGHT_PADDING_RATIO = 0.03;
const LEFT_PADDING_RATIO = 0.04;
const LEFT_PADDING_MIN_MS = 90 * 60 * 1000;
const CHART_LINE_WIDTH = 3;
const MARKER_SIZE = 12;
const MARKER_LINE_WIDTH = 6;
const HOVER_FONT_SIZE = 16;
const TICK_FONT_SIZE = 11;
const TITLE_FONT_SIZE = 12;
const Y_AXIS_DTICK = 10;
const Y_AXIS_MAX = 100;
const EnergyLossChart = ({ estimatedDays, socDropRate, socTrend }) => {
    const chartRef = useRef(null);
    const { validData } = useMemo(() => {
        const filtered = socTrend.filter((item) => !!item.timestamp && item.value !== undefined);
        const sorted = [...filtered].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const countsPerTimestamp = sorted.reduce((acc, item) => {
            acc[item.timestamp] = (acc[item.timestamp] || 0) + 1;
            return acc;
        }, {});
        const uniqueTimestampMs = Array.from(new Set(sorted.map((item) => new Date(item.timestamp).getTime()))).sort((a, b) => a - b);
        let minGapMs = Number.POSITIVE_INFINITY;
        for (let index = 1; index < uniqueTimestampMs.length; index += 1) {
            const gap = uniqueTimestampMs[index] - uniqueTimestampMs[index - 1];
            if (gap > 0 && gap < minGapMs) {
                minGapMs = gap;
            }
        }
        const fallbackGapMs = FALLBACK_GAP_MS;
        const baseGapMs = Number.isFinite(minGapMs) ? minGapMs : fallbackGapMs;
        const seenPerTimestamp = {};
        const spreadStepMs = Math.max(SPREAD_STEP_MIN_MS, Math.min(baseGapMs * SPREAD_STEP_GAP_RATIO, SPREAD_STEP_MAX_MS));
        const spreadDuplicates = sorted.map((item) => {
            const originalTimestamp = item.timestamp;
            const duplicateIndex = seenPerTimestamp[originalTimestamp] ?? 0;
            seenPerTimestamp[originalTimestamp] = duplicateIndex + 1;
            const totalDuplicates = countsPerTimestamp[originalTimestamp] || 1;
            const centeredPosition = duplicateIndex - (totalDuplicates - 1) / 2;
            const shiftedTime = new Date(new Date(originalTimestamp).getTime() + centeredPosition * spreadStepMs).toISOString();
            return {
                ...item,
                originalTimestamp,
                timestamp: shiftedTime
            };
        });
        if (spreadDuplicates.length < 2)
            return { averageDrain: "0", validData: spreadDuplicates };
        const first = spreadDuplicates[0];
        const last = spreadDuplicates[spreadDuplicates.length - 1];
        const diffValue = first.value - last.value;
        const diffTimeDays = (new Date(last.timestamp).getTime() - new Date(first.timestamp).getTime()) / MS_PER_DAY;
        return {
            averageDrain: diffTimeDays > 0 ? (diffValue / diffTimeDays).toFixed(1) : "0",
            validData: spreadDuplicates
        };
    }, [socTrend]);
    useEffect(() => {
        const chartElement = chartRef.current;
        if (!chartElement || validData.length === 0)
            return;
        const x = validData.map((item) => item.timestamp);
        const y = validData.map((item) => item.value);
        const originalTimestamps = validData.map((item) => item.originalTimestamp || item.timestamp);
        const originalTimestampMs = originalTimestamps.map((timestamp) => new Date(timestamp).getTime());
        const shiftedTimestampMs = x.map((timestamp) => new Date(timestamp).getTime());
        const minOriginalMs = Math.min(...originalTimestampMs);
        const maxOriginalMs = Math.max(...originalTimestampMs);
        const minShiftedMs = Math.min(...shiftedTimestampMs);
        const maxShiftedMs = Math.max(...shiftedTimestampMs);
        const visibleRangeMs = Math.max(maxOriginalMs - minOriginalMs, MS_PER_HOUR);
        const rightAxisPaddingMs = Math.max(MS_PER_HOUR, visibleRangeMs * RIGHT_PADDING_RATIO);
        const leftAxisPaddingMs = Math.max(LEFT_PADDING_MIN_MS, visibleRangeMs * LEFT_PADDING_RATIO);
        const xRangeStart = new Date(Math.min(minOriginalMs, minShiftedMs) - leftAxisPaddingMs).toISOString();
        const xRangeEnd = new Date(Math.max(maxOriginalMs, maxShiftedMs) + rightAxisPaddingMs).toISOString();
        const tickValues = Array.from(new Map(originalTimestamps.map((timestamp) => [timestamp.slice(0, 10), timestamp])).values());
        const trace = {
            cliponaxis: false,
            customdata: validData.map((item) => item.originalTimestamp || item.timestamp),
            fill: "tozeroy",
            fillcolor: COLOR_FILL,
            hovertemplate: [
                " ",
                " %{customdata|%m/%d/%Y, %H:%M:%S %p} ",
                " Est.Time to 0%   <b>" + estimatedDays + " days</b> ",
                " SoC              <b>%{y}%</b> ",
                " ",
                "<extra></extra>"
            ].join("<br>"),
            line: {
                color: COLOR_CHART_GREEN,
                shape: "linear",
                width: CHART_LINE_WIDTH
            },
            marker: {
                color: COLOR_CHART_MARKER_BLUE,
                line: {
                    color: COLOR_MARKER_BORDER,
                    width: MARKER_LINE_WIDTH
                },
                size: MARKER_SIZE,
                symbol: "circle"
            },
            mode: "lines+markers",
            type: "scatter",
            x,
            y
        };
        const layout = {
            autosize: true,
            dragmode: false,
            hoverlabel: {
                align: "left",
                bgcolor: "#FFFFFF",
                bordercolor: COLOR_HOVER_BORDER,
                font: {
                    color: COLOR_TITLE_FONT,
                    family: "Noto Sans;",
                    size: HOVER_FONT_SIZE
                },
                namelength: 0
            },
            hovermode: "closest",
            margin: { b: 40, l: 60, r: 20, t: 20 },
            paper_bgcolor: "white",
            plot_bgcolor: "white",
            xaxis: {
                fixedrange: true,
                range: [xRangeStart, xRangeEnd],
                rangeslider: { visible: false },
                showgrid: false,
                tickfont: { color: COLOR_AXIS_FONT, size: TICK_FONT_SIZE },
                tickformat: "%b %d",
                tickmode: "array",
                tickvals: tickValues,
                type: "date"
            },
            yaxis: {
                dtick: Y_AXIS_DTICK,
                fixedrange: true,
                gridcolor: "#F3F4F6",
                range: [0, Y_AXIS_MAX],
                tickfont: { color: COLOR_AXIS_FONT, size: TICK_FONT_SIZE },
                title: {
                    font: { color: COLOR_TITLE_FONT, size: TITLE_FONT_SIZE },
                    text: "Usable SoC (% of Total)"
                }
            }
        };
        Plotly.newPlot(chartElement, [trace], layout, { displayModeBar: false, responsive: true });
        const resizeObserver = new ResizeObserver(() => {
            Plotly.Plots.resize(chartElement);
        });
        resizeObserver.observe(chartElement);
        return () => {
            resizeObserver.disconnect();
            Plotly.purge(chartElement);
        };
    }, [validData, socDropRate]);
    return (_jsxs("div", { className: "w-full bg-white p-6 rounded-2xl shadow-sm border border-gray-100", "data-test": "energy-loss-chart-modal-chart", children: [_jsxs("div", { className: "flex justify-between items-center mb-6", "data-test": "energy-loss-chart-header", children: [_jsxs("div", { className: "text-xs text-gray-500", "data-test": "energy-loss-chart-average-drain", children: ["Average idle drain rate:", " ", _jsxs("span", { className: "font-bold text-gray-900", "data-test": "energy-loss-chart-average-drain-value", children: [socDropRate, "% per day"] })] }), _jsxs("div", { className: "flex items-center gap-2 text-xs text-gray-500", "data-test": "energy-loss-chart-legend", children: [_jsx("span", { "data-test": "energy-loss-chart-legend-label", children: "Legend:" }), _jsxs("div", { className: "flex items-center gap-1", "data-test": "energy-loss-chart-legend-icon", children: [_jsx("span", { className: "w-2 h-2 rounded-full", "data-test": "energy-loss-chart-legend-marker", style: { backgroundColor: COLOR_CHART_MARKER_BLUE } }), _jsx("span", { className: "w-4 h-[2px]", "data-test": "energy-loss-chart-legend-line", style: { backgroundColor: COLOR_CHART_GREEN } })] }), _jsx("span", { className: "font-bold text-gray-900", "data-test": "energy-loss-chart-legend-text", children: "State of Charge %" })] })] }), _jsx("div", { className: "w-full overflow-x-auto overflow-y-hidden outline-none", "data-test": "energy-loss-chart-scroll-container", children: validData.length > 0 ? (_jsx("div", { className: "h-[280px] w-full", "data-test": "energy-loss-chart-plot", ref: chartRef })) : (_jsx("div", { className: "h-[280px] w-full flex items-center justify-center text-sm text-gray-500", "data-test": "energy-loss-chart-no-data", children: "No data" })) }), _jsx("div", { className: "w-full text-center text-[11px] font-bold text-gray-900 mt-4 uppercase tracking-wider", "data-test": "energy-loss-chart-x-axis-label", children: "Idling Time (h)" })] }));
};
export default EnergyLossChart;
